import { useEffect } from 'react';

import { LogEvent } from '@analytics';
import { useGetUser } from '@db/collections';
import * as Dialog from '@radix-ui/react-dialog';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from '@uidotdev/usehooks';
import { NewBoxButton, NewIcon, Spacer, useToast } from 'design-system';
import { db } from 'firebase-config';
import { doc, updateDoc } from 'firebase/firestore';
import Image from 'next/image';
import { useQueryState } from 'nuqs';

import { MarketingAgreementBottomSheet } from '@components/MarketingAgreementBottomSheet';
import { NeedLoginBottomSheet } from '@components/NeedLoginBottomSheet/NeedLoginBottomSheet';

import { api } from '@apis/hc';

export const KnockCouponModal = ({ close }: { close: () => void }) => {
  const [knockCode] = useQueryState('knock_code', {
    defaultValue: '',
  });

  const [, setUnreceivedCoupon] = useLocalStorage('last-unreceived-coupon-code', '');
  const { user } = useGetUser();

  const { openToast } = useToast();

  const { data, isError } = useQuery({
    queryKey: ['knock-coupon', knockCode],
    queryFn: async () => {
      const { status, data } = await (
        await api.event.knock['get-coupon'].$post({
          json: {
            couponVoucherCode: knockCode,
          },
        })
      ).json();

      if (status === 'success' && data) {
        return data.couponCode;
      }

      throw new Error('노크로 돌아가서 다시 쿠폰 받기를 눌러주세요.');
    },
    retry: false,
    enabled: !!knockCode,
  });

  useEffect(() => {
    if (data) {
      setUnreceivedCoupon(data);
    }
  }, [data]);

  useEffect(() => {
    if (isError) {
      openToast({
        title: '노크로 돌아가서 다시 쿠폰 받기를 눌러주세요.',
      });
    }
  }, [isError, openToast]);

  if (!data) return null;

  return (
    <Dialog.Root open>
      <Dialog.Portal
        forceMount
        container={typeof document !== 'undefined' ? document.getElementById('portal') : undefined}
      >
        <Dialog.Overlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0  bg-new-black-50 fixed inset-0 z-[900]" />
        <Dialog.Content className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-[1000] flex w-[312px] translate-x-[-50%] translate-y-[-50%] flex-col items-center rounded-[24px] border bg-white duration-200">
          <Dialog.Close className="absolute right-[16px] top-[16px] z-[1100]" onClick={close}>
            <NewIcon icon="x-outlined" width={24} height={24} className="fill-new-gray-900" />
          </Dialog.Close>
          <Spacer className="h-32" />
          <Image
            className="h-[107px] w-[152px]"
            src="/events/knock/knock-coupon.png"
            alt="할인 쿠폰"
            width={456}
            height={323}
          />
          <div className="px-16">
            <Spacer className="h-24" />
            <p className="text-new-Sub-Title">노크 회원 전용 할인 쿠폰!</p>
            <Spacer className="h-8" />
            <p className="text-new-Body2-medium text-new-gray-500 text-center">
              10,000원 할인 쿠폰이 발급되었습니다.
              <br />
              지금 바로 받고 사용해보세요!
            </p>
            <Spacer className="h-24" />
            <MarketingAgreementBottomSheet
              onSubmit={async () => {
                if (!user) return;
                await user.addCoupon({
                  code: data,
                });

                LogEvent.유입.knockCouponAction('receive_coupon');

                openToast({
                  title: '노크 회원 전용 쿠폰이 발급되었습니다.',
                });

                if (knockCode.length) {
                  await updateDoc(doc(db, 'CouponVoucher', knockCode), {
                    isUsed: true,
                  });
                }

                setUnreceivedCoupon('');

                close();
              }}
            >
              <NeedLoginBottomSheet>
                <NewBoxButton label="할인 쿠폰 받기" styles="filled-yellow" fill />
              </NeedLoginBottomSheet>
            </MarketingAgreementBottomSheet>
            <Spacer className="h-24" />
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
